// Utility to fetch and parse RSS/Atom feeds using fast-xml-parser
// Note: Many RSS servers do not set CORS headers; browser fetch may fail due to CORS.
// This utility does not use any proxy as requested. If fetch fails with CORS errors,
// you must retrieve the feed server-side or adjust server CORS settings.

import { XMLParser } from 'fast-xml-parser';

/**
 * Normalize an RSS 2.0 or Atom 1.0 feed object into a common shape.
 * @param {object} raw Parsed XML object from fast-xml-parser.
 * @returns {{meta: object, items: Array<object>}}
 */
export function normalizeFeed(raw) {
  if (!raw || typeof raw !== 'object') {
    return { meta: {}, items: [] };
  }

  // RSS 2.0 typically at raw.rss.channel
  if (raw.rss && raw.rss.channel) {
    const ch = raw.rss.channel;
    const items = Array.isArray(ch.item) ? ch.item : (ch.item ? [ch.item] : []);
    return {
      meta: {
        type: 'rss2',
        title: ch.title || '',
        link: ch.link || '',
        description: ch.description || '',
        language: ch.language || '',
        updated: ch.lastBuildDate || ch.pubDate || '',
      },
      items: items.map(it => {
        const media = normalizeMediaContent(it['media:content']);
        const enclosure = normalizeEnclosure(it.enclosure) || (media && media.length ? {
          url: media[0].url,
          type: media[0].type,
          length: media[0].length,
        } : null);
        return ({
          id: it.guid && (typeof it.guid === 'object' ? it.guid['#text'] || '' : it.guid) || it.link || it.title || '',
          title: it.title || '',
          link: it.link || '',
          description: it.description || it['content:encoded'] || '',
          content: it['content:encoded'] || it.description || '',
          pubDate: it.pubDate || '',
          author: it.author || (it['dc:creator'] || ''),
          categories: normalizeToArray(it.category),
          enclosure,
          media,
        });
      })
    };
  }

  // Atom 1.0 typically at raw.feed
  if (raw.feed) {
    const feed = raw.feed;
    const entries = normalizeToArray(feed.entry);
    return {
      meta: {
        type: 'atom',
        title: getText(feed.title),
        link: pickAtomLink(feed.link),
        description: getText(feed.subtitle) || '',
        language: feed['xml:lang'] || '',
        updated: feed.updated || '',
      },
      items: entries.map(e => {
        const media = normalizeMediaContent(e['media:content']);
        const enclosure = pickAtomEnclosure(e.link) || (media && media.length ? {
          url: media[0].url,
          type: media[0].type,
          length: media[0].length,
        } : null);
        return ({
          id: e.id || pickAtomLink(e.link) || getText(e.title) || '',
          title: getText(e.title) || '',
          link: pickAtomLink(e.link) || '',
          description: getText(e.summary) || '',
          content: getText(e.content) || getText(e.summary) || '',
          pubDate: e.updated || e.published || '',
          author: extractAtomAuthor(e.author),
          categories: normalizeToArray(e.category).map(c => c.term || c),
          enclosure,
          media,
        });
      })
    };
  }

  // Fallback: attempt to find channel/item-like structures
  const maybeItems = normalizeToArray(raw.item || raw.items || []);
  return { meta: { type: 'unknown' }, items: maybeItems };
}

function normalizeToArray(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function normalizeEnclosure(enc) {
  if (!enc) return null;
  if (Array.isArray(enc)) enc = enc[0];
  if (typeof enc === 'object') {
    return {
      url: enc.url || enc.href || '',
      type: enc.type || '',
      length: enc.length != null ? Number(enc.length) : undefined,
    };
  }
  return { url: String(enc) };
}

function normalizeMediaContent(mc) {
  if (!mc) return [];
  const arr = Array.isArray(mc) ? mc : [mc];
  return arr
    .map(o => {
      if (o == null) return null;
      if (typeof o === 'string') return { url: o };
      if (typeof o === 'object') {
        const m = {
          url: o.url || o.href || '',
          type: o.type || '',
          medium: o.medium || '',
          width: o.width != null ? Number(o.width) : undefined,
          height: o.height != null ? Number(o.height) : undefined,
          length: o.fileSize != null ? Number(o.fileSize) : (o.length != null ? Number(o.length) : undefined),
        };
        return m;
      }
      return null;
    })
    .filter(Boolean);
}

function getText(node) {
  if (node == null) return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'object') {
    // fast-xml-parser may store text content under '#text'
    if (node['#text']) return node['#text'];
    // or as value if it's just a string-like object
    if (node.text) return node.text;
  }
  return '';
}

function pickAtomLink(link) {
  const links = normalizeToArray(link);
  if (!links.length) return '';
  // Prefer rel="alternate", then first href
  const alt = links.find(l => (l.rel || 'alternate') === 'alternate' && l.href);
  return (alt && alt.href) || (links[0] && links[0].href) || '';
}

function pickAtomEnclosure(link) {
  const links = normalizeToArray(link);
  const enc = links.find(l => l.rel === 'enclosure');
  if (!enc) return null;
  return normalizeEnclosure(enc);
}

function extractAtomAuthor(author) {
  const a = Array.isArray(author) ? author[0] : author;
  if (!a) return '';
  return a.name || a.email || a.uri || '';
}

/**
 * Parse an RSS/Atom XML string to a normalized object.
 * @param {string} xmlString
 * @returns {{meta: object, items: Array<object>}}
 */
export function parseFeed(xmlString) {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '',
  });
  const raw = parser.parse(xmlString);
  return normalizeFeed(raw);
}

/**
 * Fetch and parse an RSS/Atom feed from a URL.
 * Note: In Browser-Umgebungen kann dies an CORS scheitern, wenn der Server keine passenden Header sendet.
 * @param {string} url
 * @returns {Promise<{meta: object, items: Array<object>}>}
 */
export async function fetchAndParseFeed(url) {
  const res = await fetch(url, { headers: { 'Accept': 'application/rss+xml, application/atom+xml, application/xml, text/xml, */*' } });
  if (!res.ok) {
    throw new Error(`Failed to fetch feed: ${res.status} ${res.statusText}`);
  }
  const text = await res.text();
  return parseFeed(text);
}

export default {
  parseFeed,
  fetchAndParseFeed,
};
